import React, { useEffect, useRef, useState } from 'react';
import {
  RACButton,
  RACCard,
  makeStyles,
  Grid,
  Typography,
} from '@rentacenter/racstrap';
import clsx from 'clsx';
import { ReactComponent as StoreFront } from '../../../assets/images/storefront.svg';
import { ShopLocalProductModal } from './shopLocalProductModal';
import { ShopLocalStatusModal } from './shopLocalStatusModal';
import { useShopLocalActions } from './ShopLocalProvider';
import {
  useNationalPartnerData,
  useNationalPartnerActions,
} from '../NationalPartnerProvider';
import { FundingInProgressWarningModal } from '../FundingInProgressWarningModal';

const useStyles = makeStyles((theme: any) => ({
  shopLocalsRoot: {
    width: '100%',
    height: '100%',
    padding: '0 0 0 0.5rem',
    overflow: 'hidden',
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    marginBottom: '0.5rem',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  cardBody: {
    flex: '1 1 auto',
    padding: '1rem 1rem',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: 0,
  },
  shopLocalHeader: {
    fontSize: '1rem',
  },
  shopLocalInnerHeader: {
    padding: '1rem',
    fontSize: '0.875rem',
  },
  shopLocalInnerBody: {
    padding: '1rem',
    width: '100%',
    fontSize: '0.75rem',
  },
  colorit: {
    backgroundColor: '#777777',
  },
  innerboxContainer: {
    alignContent: 'center',
    alignItems: 'center',
    justifyItems: 'center',
    verticalAlign: 'middle',
  },
  shopLocalBody: {
    padding: '3rem',
    fontSize: '1rem',
  },
  innerbox: {
    backgroundColor: '#E8F1FE',
    border: '1px solid #D6E4F8',
    alignContent: 'center',
    alignItems: 'center',
    justifyItems: 'center',
    verticalAlign: 'middle',
    flex: 1,
    borderRadius: 8,
    padding: '0.75rem',
  },
  storeFrontImage: {
    width: '100%',
    height: '100%',
    display: 'block',
  },
  grid: {
    display: 'flex',
    justifyContent: 'flex-start',
    gap: theme.typography.pxToRem(24),
  },
  button: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  buttonContainer: {
    width: '100%',
  },
  noPaddingLeft: {
    paddingLeft: '0 !important',
  },
}));

export const ShopLocal = () => {
  const classes = useStyles();
  const [shopLocalModal, setShopLocalModal] = useState<boolean>(false);
  const [shopLocalStatus, setShopLocalStatus] = useState<boolean>(false);
  const [showWarningModal, setShowWarningModal] = useState<boolean>(false);
  const reUseItem = useRef<boolean>(false);

  const { resetProducts } = useShopLocalActions();
  const {
    isProcessing,
    fundingState,
    vendorName,
    approvalLimitAmount,
    resolvedProducts,
    checkoutState,
    spendLimitId,
    checkoutFailedAcknowledged,
  } = useNationalPartnerData();
  const { setCheckoutFailedAcknowledged } = useNationalPartnerActions();

  const processNextModal = (processIt: boolean) => {
    setShopLocalModal(false); // bring down the primary modal

    //launch the second modal
    setShopLocalStatus(processIt);
  };

  const finalizeShopLocal = () => {
    setShopLocalStatus(false);
    resetProducts();
  };
  const processStatusClosed = (popUpProductModal: any) => {
    if (popUpProductModal) {
      reUseItem.current = true;
      setShopLocalModal(true);
    } else {
      resetProducts();
    }

    setShopLocalStatus(false);
  };

  useEffect(() => {
    resetProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={clsx(classes.shopLocalsRoot)}>
      <RACCard className={clsx(classes.card)}>
        <div className={clsx(classes.cardBody)}>
          <div className={clsx(classes.row)}>
            <Grid container spacing={0}>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                lg={12}
                classes={{ root: classes.grid }}
              >
                <Typography
                  className={clsx(classes.shopLocalHeader)}
                  variant="h6"
                >
                  {'Shop Local'}{' '}
                </Typography>
              </Grid>
            </Grid>
          </div>
          <div className={clsx(classes.innerbox)}>
            <Grid
              container
              spacing={1}
              className={clsx(classes.innerboxContainer)}
            >
              <Grid item xs={2} sm={2} md={2} lg={2}>
                <StoreFront
                  style={{
                    height: '70%',
                    width: '70%',
                    marginLeft: '8px',
                    display: 'block',
                    verticalAlign: 'middle',
                  }}
                />
              </Grid>
              <Grid item xs={10} sm={10} md={10} lg={10}>
                <Typography
                  style={{
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    marginBottom: '4px',
                  }}
                >
                  {'Shop Local Stores'}
                </Typography>
                <Typography
                  style={{
                    fontSize: '0.75rem',
                    color: '#555',
                    marginBottom: '6px',
                  }}
                >
                  {
                    'Use this option to secure a credit amount on your card before shopping'
                  }
                </Typography>
                <RACButton
                  // eslint-disable-next-line
                  // @ts-ignore
                  size="small"
                  isRounded={false}
                  variant="contained"
                  color="primary"
                  disabled={isProcessing}
                  style={{
                    fontSize: '0.75rem',
                    padding: '4px 12px',
                  }}
                  onClick={() => {
                    // If funding is approved and purchase not completed, show warning
                    if (
                      fundingState === 'APPROVED' &&
                      checkoutState !== 'PURCHASE_SUCCESSFUL'
                    ) {
                      if (
                        checkoutState === 'CHECKOUT_FAILED' &&
                        checkoutFailedAcknowledged
                      ) {
                        // Already acknowledged — let them through
                      } else {
                        setShowWarningModal(true);
                        return;
                      }
                    }
                    reUseItem.current = false;
                    setShopLocalModal(true);
                  }}
                >
                  {'Shop Now'}
                </RACButton>
              </Grid>
            </Grid>
          </div>
        </div>
        {shopLocalModal && (
          <ShopLocalProductModal
            editMode={reUseItem.current}
            onClose={() => setShopLocalModal(false)}
            onProcess={processNextModal}
          />
        )}
        {shopLocalStatus && (
          <ShopLocalStatusModal
            onClose={processStatusClosed}
            finalizeShopLocal={finalizeShopLocal}
          />
        )}

        {/* Warning modal when funding is approved and user tries to click Shop Now */}
        <FundingInProgressWarningModal
          isOpen={showWarningModal}
          vendorName={vendorName}
          productDescription={
            resolvedProducts && resolvedProducts.length > 0
              ? resolvedProducts
                  .map((p) => {
                    return p.description || p.brand || '';
                  })
                  .filter(Boolean)
                  .join(', ')
              : ''
          }
          approvedAmount={approvalLimitAmount}
          onClose={() => setShowWarningModal(false)}
          onOk={() => {
            setShowWarningModal(false);
            if (checkoutState === 'CHECKOUT_FAILED') {
              setCheckoutFailedAcknowledged(true);
            }
          }}
          checkoutFailed={checkoutState === 'CHECKOUT_FAILED'}
          spendLimitId={spendLimitId}
        />
      </RACCard>
    </div>
  );
};
